import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PasswordInput from '@/components/PasswordInput';
import { useAuth } from '@/hooks/useAuth';
import { getPostAuthRedirect, getRequestedRedirect, withNextParam } from '@/utils/authRedirect';
import styles from '@/widgets/LoginForm/styles.module.scss';
import { VERBATIM_INPUT_PROPS } from '@/constants';

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordConfirm: string;
}

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="#0496FF"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" /></svg>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16"><path fill="#EA4335" d="M12 11v3.2h4.5c-.2 1.2-1.4 3.4-4.5 3.4-2.7 0-4.9-2.2-4.9-5s2.2-5 4.9-5c1.5 0 2.6.6 3.2 1.2l2.2-2.1C15.9 5.3 14.1 4.5 12 4.5 7.9 4.5 4.5 7.9 4.5 12s3.4 7.5 7.5 7.5c4.3 0 7.2-3 7.2-7.3 0-.5 0-.9-.1-1.2H12z" /></svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M16.4 12.7c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.9-.9-3.1-.9-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.6.8 1.2 1.7 2.5 3 2.4 1.2 0 1.6-.8 3.1-.8 1.4 0 1.8.8 3.1.8 1.3 0 2.1-1.2 2.9-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.7-1-2.7-3.9zM14.2 5.7c.6-.8 1-1.9.9-3-1 0-2.1.7-2.8 1.5-.6.7-1.1 1.8-1 2.9 1.1.1 2.3-.6 2.9-1.4z" /></svg>
);

const ArrowIcon = () => (
  <svg className={styles.arrow} width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden="true">
    <path d="M1 6h12m0 0-5-5m5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const SignUpForm: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const requestedRedirect = getRequestedRedirect(location);
  const redirectTo = getPostAuthRedirect(location, '/');

  const {
    register: registerUser,
    registerWithTelegram,
    registerWithGoogle,
    registerWithApple,
    isLoading,
    isTelegramAvailable,
  } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm<SignUpFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      passwordConfirm: '',
    },
  });

  const onSubmit = async (data: SignUpFormData) => {
    try {
      await registerUser({
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        email: data.email.trim().toLowerCase(),
        password: data.password,
      });
      toast.success(t('accountCreated'));
      navigate(redirectTo, { replace: true });
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t('signUpError'));
    }
  };

  const onValidationError = () => {
    // Blur active input to close keyboard, then scroll to top so toast is visible
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (errors.firstName) toast.error(t('firstNameRequired'));
    else if (errors.email) toast.error(t('emailInvalid'));
    else if (errors.password) toast.error(t('passwordMinLength'));
    else if (errors.passwordConfirm) toast.error(String(errors.passwordConfirm.message || t('confirmPassword')));
  };

  const handleSocialRegister = async (method: () => Promise<any>, errorMsg: string) => {
    try {
      await method();
      navigate(redirectTo, { replace: true });
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || errorMsg);
    }
  };

  return (
    <article className={styles.loginCard}>
      <div className={styles.cardHead}>
        <span className={styles.eyebrow}>{t('joinPClash')}</span>
        <h2 className={styles.cardTitle}>{t('createAccountAction')}</h2>
        <p className={styles.cardSub}>{t('fillFormOrSocial')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onValidationError)}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="signup-firstname">{t('firstName')}</label>
          <input
            className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
            id="signup-firstname"
            type="text"
            placeholder="John"
            disabled={isLoading}
            data-testid="signup-firstname"
            {...register('firstName', { required: true })}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="signup-lastname">{t('lastName')}</label>
          <input
            className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
            id="signup-lastname"
            type="text"
            placeholder="Doe"
            disabled={isLoading}
            data-testid="signup-lastname"
            {...register('lastName')}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="signup-email">{t('email')}</label>
          <input
            className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
            id="signup-email"
            type="text"
            placeholder="you@example.com"
            disabled={isLoading}
            data-testid="signup-email"
            autoComplete="email"
            {...VERBATIM_INPUT_PROPS}
            {...register('email', {
              required: true,
              pattern: /^\S+@\S+$/i,
            })}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="signup-password">{t('password')}</label>
          <Controller
            control={control}
            name="password"
            rules={{ required: true, minLength: 6 }}
            render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
              <PasswordInput
                error={!!error}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t('minCharsPassword')}
                innerRef={ref}
                disabled={isLoading}
                id="signup-password"
                data-testid="signup-password"
              />
            )}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="signup-confirm">{t('confirmPassword')}</label>
          <Controller
            control={control}
            name="passwordConfirm"
            rules={{
              required: true,
              validate: (value) => value === watch('password') || t('passwordsDoNotMatch'),
            }}
            render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
              <PasswordInput
                error={!!error}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t('repeatPassword')}
                innerRef={ref}
                disabled={isLoading}
                id="signup-confirm"
                data-testid="signup-confirm"
              />
            )}
          />
        </div>

        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          type="submit"
          disabled={isLoading}
          data-testid="signup-submit"
          style={{ marginTop: 8 }}
        >
          {isLoading ? t('creating') : t('createAccountAction')}
          {!isLoading && <ArrowIcon />}
        </button>
      </form>

      <div className={styles.divider}>
        <span />
        <em>{t('orContinueWith')}</em>
        <span />
      </div>

      <div className={styles.socials}>
        {isTelegramAvailable && (
          <button
            type="button"
            className={styles.social}
            onClick={() => handleSocialRegister(registerWithTelegram, t('telegramSignInError'))}
            disabled={isLoading}
          >
            <TelegramIcon />
            {t('telegram')}
          </button>
        )}
        <button
          type="button"
          className={styles.social}
          onClick={() => handleSocialRegister(registerWithGoogle, t('googleSignInError'))}
          disabled={isLoading}
        >
          <GoogleIcon />
          {t('google')}
        </button>
        <button
          type="button"
          className={styles.social}
          onClick={() => handleSocialRegister(registerWithApple, t('appleSignInError'))}
          disabled={isLoading}
        >
          <AppleIcon />
          {t('apple')}
        </button>
      </div>

      <div className={styles.cardFoot}>
        <span>{t('alreadyHaveAccount')}</span>
        <Link
          to={withNextParam('/login', requestedRedirect)}
          className={styles.textBtn}
          data-testid="signup-login-link"
        >
          {t('signInAction')}
        </Link>
      </div>
    </article>
  );
};

export default SignUpForm;
